/* Where a claim comes from.
 *
 * Every formula, explanation and further-reading list on the path points at a
 * real paper. `read` on a block names them directly; READING names the ones a
 * block inherits, and the bibliography in the About modal groups the whole set
 * by subject.
 */
import { READING } from '../data/reading';
import { REFS } from '../data/refs';
import { TOPIC_ORDER } from '../data/topics';
import type { Block, RefRow, TopicKey } from '../types';

/* A block's sources: the ones it names, then the ones it inherits, each once
   and each resolving to a real entry. */
export function blockRead(block: Block): string[] {
  const keys = [...(block.read ?? []), ...(READING[block.id] ?? [])];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const key of keys) {
    if (!REFS[key] || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

export interface TopicGroup {
  topic: TopicKey;
  rows: RefRow[];
}

/* Grouped by subject, in the authored order, each paper listed once even when
   several blocks cite it.
 *
 * The de-duplication key is `id || v`, not `id ?? v`: six entries carry an empty
 * string as their id and a venue instead, and a nullish fallback would collapse
 * all six into one.
 */
export function bibliography(): TopicGroup[] {
  const seen = new Set<string>();
  const groups: TopicGroup[] = [];
  for (const topic of TOPIC_ORDER) {
    const rows: RefRow[] = [];
    for (const row of Object.values(REFS)) {
      if (row.topic !== topic) continue;
      const key = row.id || row.v || row.t;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push(row);
    }
    if (rows.length) groups.push({ topic, rows });
  }
  return groups;
}

/* Papers are cited by arXiv identifier where one exists, otherwise by venue. */
export function refLabel(row: RefRow): string {
  return row.id ? `arXiv:${row.id}` : (row.v ?? '');
}

export function refHref(row: RefRow): string | null {
  return row.id ? `https://arxiv.org/abs/${row.id}` : null;
}

/* The bibliography prints the identifier as text rather than as a link, which
   is how the artifact drew it. */
export function bibLabel(row: RefRow): string {
  return row.id ? `arXiv:${row.id}` : (row.v ?? '');
}
